import parser from 'any-date-parser';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Center,
  Checkbox,
  Container,
  Divider,
  Grid,
  Group,
  Loader,
  NumberInput,
  Select,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCheck, IconLogout } from '@tabler/icons-react';
import { handleSubmitError } from '@utils/forms';

import {
  useFindCustomerQuery,
  useUpdateCustomerMutation,
} from '@api/customers.api';

import useAuth from '@hooks/useAuth';
import useFormEnhanced from '@hooks/useFormEnhanced';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from '../common/locations';
import classes from './Account.module.css';

export default function Account() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { logout } = useAuth();
  const { t } = useTranslation();

  // ==========================================================================
  // State
  // ==========================================================================
  const [isDomicileDifferent, setIsDomicileDifferent] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: current, isLoading } = useFindCustomerQuery();
  const [updateCustomer, { isLoading: isUpdating }] =
    useUpdateCustomerMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    firstName: current?.user.firstName || '',
    lastName: current?.user.lastName || '',
    companyName: current?.companyName || '',
    vatTaxNumber: current?.vatTaxNumber || '',
    pec: current?.pec || '',
    sdi: current?.sdi || '',
    gender: current?.gender || undefined,
    address: current?.address || '',
    city: current?.city || '',
    zip: current?.zip || '',
    province: current?.province || '',
    birthDate: current?.birthDate
      ? new Date(current.birthDate).toISOString()
      : undefined,
    birthCity: current?.birthCity || '',
    height: current?.height || undefined,
    country: current?.country || '',
    nationality: current?.nationality || '',
    phone: current?.phone || '',
    domicileCountry: current?.domicileCountry || '',
    domicileProvince: current?.domicileProvince || '',
    domicileCity: current?.domicileCity || '',
    domicileZip: current?.domicileZip || '',
    domicileAddress: current?.domicileAddress || '',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: [
        'firstName',
        'lastName',
        'companyName',
        'birthCity',
        'country',
        'province',
        'city',
        'address',
        'nationality',
        'domicileCountry',
        'domicileProvince',
        'domicileCity',
        'domicileAddress',
      ],
      lowercaseFields: ['pec'],
      uppercaseFields: ['vatTaxNumber', 'sdi'],
    },
  });

  useEffect(() => {
    if (current) {
      form.setValues({
        firstName: current?.user.firstName || '',
        lastName: current?.user.lastName || '',
        companyName: current?.companyName || '',
        vatTaxNumber: current?.vatTaxNumber || '',
        pec: current?.pec || '',
        sdi: current?.sdi || '',
        gender: current?.gender || undefined,
        address: current?.address || '',
        city: current?.city || '',
        zip: current?.zip || '',
        province: current?.province || '',
        birthDate: current?.birthDate
          ? new Date(current.birthDate).toISOString()
          : undefined,
        birthCity: current?.birthCity || '',
        height: current?.height || undefined,
        country: current?.country || '',
        nationality: current?.nationality || '',
        phone: current?.phone || '',
        domicileCountry: current?.domicileCountry || '',
        domicileProvince: current?.domicileProvince || '',
        domicileCity: current?.domicileCity || '',
        domicileZip: current?.domicileZip || '',
        domicileAddress: current?.domicileAddress || '',
      });

      // Also update the domicile checkbox if needed
      if (
        current.type === 'private' &&
        (current.domicileAddress !== current.address ||
          current.domicileCity !== current.city ||
          current.domicileCountry !== current.country ||
          current.domicileProvince !== current.province ||
          current.domicileZip !== current.zip)
      ) {
        setIsDomicileDifferent(true);
      }
    }
  }, [current]);

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.companyName.trim() || undefined,
        vatTaxNumber: values.vatTaxNumber,
        pec: values.pec?.trim() || undefined,
        sdi: values.sdi?.trim() || undefined,
        gender: values.gender || undefined,
        birthDate: values.birthDate
          ? new Date(values.birthDate).toISOString()
          : undefined,
        birthCity: values.birthCity || undefined,
        height: values.height || undefined,
        phone: values.phone,
        country: values.country,
        province: values.province,
        city: values.city,
        zip: values.zip,
        address: values.address,
        nationality: values.nationality?.trim() || undefined,
        domicileCountry:
          current?.type === 'private' && isDomicileDifferent
            ? values.domicileCountry
            : values.country,
        domicileProvince:
          current?.type === 'private' && isDomicileDifferent
            ? values.domicileProvince
            : values.province,
        domicileCity:
          current?.type === 'private' && isDomicileDifferent
            ? values.domicileCity
            : values.city,
        domicileZip:
          current?.type === 'private' && isDomicileDifferent
            ? values.domicileZip
            : values.zip,
        domicileAddress:
          current?.type === 'private' && isDomicileDifferent
            ? values.domicileAddress
            : values.address,
      };

      await updateCustomer(mappedValues).unwrap();

      setIsSuccess(true);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container mt={{ base: 'md', xs: 0 }}>
      <Group justify="space-between">
        <Title mb="md" order={2}>
          {t('account.title')} (
          {current?.type === 'private'
            ? t('account.type.private')
            : t('account.type.company')}
          )
        </Title>
        <Group>
          <Button onClick={logout} leftSection={<IconLogout />}>
            {t('account.logout')}
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Center>
          <Loader />
        </Center>
      ) : current ? (
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Divider
            label={t('account.baseInfo')}
            labelPosition="left"
            mt="xl"
            mb="sm"
            classNames={{ label: classes.dividerLabel }}
          />

          <Grid>
            <Grid.Col span={{ md: 6 }}>
              <TextInput
                label={t('account.fields.firstName.label')}
                placeholder={t('account.fields.firstName.placeholder')}
                required
                {...form.getInputProps('firstName')}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
              <TextInput
                label={t('account.fields.lastName.label')}
                placeholder={t('account.fields.lastName.placeholder')}
                required
                {...form.getInputProps('lastName')}
              />
            </Grid.Col>

            <Grid.Col span={{ md: 6 }}>
              <TextInput
                label={
                  current?.type === 'company'
                    ? t('account.fields.vatTaxNumber.labelCompany')
                    : t('account.fields.vatTaxNumber.labelPrivate')
                }
                placeholder={
                  current?.type === 'company'
                    ? t('account.fields.vatTaxNumber.placeholderCompany')
                    : t('account.fields.vatTaxNumber.placeholderPrivate')
                }
                required
                {...form.getInputProps('vatTaxNumber')}
              />
            </Grid.Col>

            <Grid.Col span={{ md: 6 }}>
              {current?.type === 'company' ? (
                <TextInput
                  label={t('account.fields.companyName.label')}
                  placeholder={t('account.fields.companyName.placeholder')}
                  required
                  {...form.getInputProps('companyName')}
                />
              ) : (
                <Select
                  label={t('account.fields.gender.label')}
                  placeholder={t('account.fields.gender.placeholder')}
                  required
                  data={[
                    { label: t('account.fields.gender.male'), value: 'male' },
                    {
                      label: t('account.fields.gender.female'),
                      value: 'female',
                    },
                  ]}
                  {...form.getInputProps('gender')}
                />
              )}
            </Grid.Col>

            {current?.type === 'company' && (
              <>
                <Grid.Col span={{ md: 6 }}>
                  <TextInput
                    label={t('account.fields.pec.label')}
                    placeholder={t('account.fields.pec.placeholder')}
                    {...form.getInputProps('pec')}
                  />
                </Grid.Col>
                <Grid.Col span={{ md: 6 }}>
                  <TextInput
                    label={t('account.fields.sdi.label')}
                    placeholder={t('account.fields.sdi.placeholder')}
                    {...form.getInputProps('sdi')}
                  />
                </Grid.Col>
              </>
            )}

            <Grid.Col span={{ md: 6 }}>
              <TextInput
                label={t('account.fields.phone.label')}
                placeholder={t('account.fields.phone.placeholder')}
                required
                {...form.getInputProps('phone')}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
              {current?.type === 'private' && (
                <NumberInput
                  label={t('account.fields.height.label')}
                  placeholder={t('account.fields.height.placeholder')}
                  required
                  min={50}
                  {...form.getInputProps('height')}
                />
              )}
            </Grid.Col>

            {current?.type === 'private' && (
              <>
                <Grid.Col span={{ md: 6 }}>
                  <DateInput
                    label={t('account.fields.birthDate.label')}
                    placeholder={t('account.fields.birthDate.placeholder')}
                    required
                    valueFormat="DD/MM/YYYY"
                    dateParser={(value) =>
                      parser.fromString(value, 'it').toISOString()
                    }
                    {...form.getInputProps('birthDate')}
                  />
                </Grid.Col>
                <Grid.Col span={{ md: 6 }}>
                  <TextInput
                    label={t('account.fields.birthCity.label')}
                    placeholder={t('account.fields.birthCity.placeholder')}
                    required
                    {...form.getInputProps('birthCity')}
                  />
                </Grid.Col>
              </>
            )}

            {current?.type === 'private' && (
              <Grid.Col span={{ md: 6 }}>
                <TextInput
                  label={t('account.fields.nationality.label')}
                  placeholder={t('account.fields.nationality.placeholder')}
                  required
                  {...form.getInputProps('nationality')}
                />
              </Grid.Col>
            )}
          </Grid>

          <Divider
            label={t('account.residence')}
            labelPosition="left"
            mt="xl"
            mb="sm"
            classNames={{ label: classes.dividerLabel }}
          />

          <Grid>
            <Grid.Col span={{ md: 6 }}>
              <NationSelector
                placeholder={t('account.fields.country.placeholder')}
                required
                {...form.getInputProps('country')}
              />
            </Grid.Col>
            <Grid.Col span={{ md: 6 }}>
              {form.values.country === 'Italia' ? (
                <ProvinceSelector
                  required
                  placeholder={t('account.fields.province.placeholder')}
                  {...form.getInputProps('province')}
                />
              ) : (
                <TextInput
                  label={t('account.fields.city.label')}
                  placeholder={t('account.fields.city.placeholderResidence')}
                  required
                  {...form.getInputProps('city')}
                />
              )}
            </Grid.Col>

            {form.values.country === 'Italia' && (
              <>
                <Grid.Col span={{ md: 6 }}>
                  <CitySelector
                    required
                    placeholder={t('account.fields.city.placeholderResidence')}
                    province={form.values.province}
                    {...form.getInputProps('city')}
                  />
                </Grid.Col>
                <Grid.Col span={{ md: 6 }}>
                  <ZipSelector
                    required
                    placeholder={t('account.fields.zip.placeholder')}
                    city={form.values.city}
                    {...form.getInputProps('zip')}
                  />
                </Grid.Col>
              </>
            )}

            <Grid.Col span={12}>
              <TextInput
                label={t('account.fields.address.label')}
                placeholder={t('account.fields.address.placeholderResidence')}
                required
                {...form.getInputProps('address')}
              />
            </Grid.Col>
          </Grid>

          {current?.type === 'private' && (
            <>
              <Divider
                label={t('account.domicile')}
                labelPosition="left"
                mt="xl"
                mb="sm"
                classNames={{ label: classes.dividerLabel }}
              />

              <Checkbox
                w="max-content"
                mx="auto"
                mb="lg"
                label={t('account.domicileDifferent')}
                checked={isDomicileDifferent}
                onChange={(e) =>
                  setIsDomicileDifferent(e.currentTarget.checked)
                }
              />

              {isDomicileDifferent && (
                <Grid>
                  <Grid.Col span={{ md: 6 }}>
                    <NationSelector
                      placeholder={t(
                        'account.fields.domicileCountry.placeholder',
                      )}
                      required
                      {...form.getInputProps('domicileCountry')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ md: 6 }}>
                    {form.values.domicileCountry === 'Italia' ? (
                      <ProvinceSelector
                        required
                        placeholder={t(
                          'account.fields.domicileProvince.placeholder',
                        )}
                        {...form.getInputProps('domicileProvince')}
                      />
                    ) : (
                      <TextInput
                        label={t('account.fields.city.label')}
                        placeholder={t(
                          'account.fields.city.placeholderDomicile',
                        )}
                        required
                        {...form.getInputProps('domicileCity')}
                      />
                    )}
                  </Grid.Col>

                  {form.values.domicileCountry === 'Italia' && (
                    <>
                      <Grid.Col span={{ md: 6 }}>
                        <CitySelector
                          required
                          placeholder={t(
                            'account.fields.domicileCity.placeholder',
                          )}
                          province={form.values.domicileProvince}
                          {...form.getInputProps('domicileCity')}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ md: 6 }}>
                        <ZipSelector
                          required
                          placeholder={t(
                            'account.fields.domicileZip.placeholder',
                          )}
                          city={form.values.domicileCity}
                          {...form.getInputProps('domicileZip')}
                        />
                      </Grid.Col>
                    </>
                  )}

                  <Grid.Col span={12}>
                    <TextInput
                      label={t('account.fields.address.label')}
                      placeholder={t(
                        'account.fields.address.placeholderDomicile',
                      )}
                      required
                      {...form.getInputProps('domicileAddress')}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}

          {form.errors.general && (
            <Text c="red" size="sm" mt="xl" ta="center">
              {form.errors.general}
            </Text>
          )}

          {isSuccess && (
            <Alert color="green" mt="xl" icon={<IconCheck />}>
              {t('account.successMessage')}
            </Alert>
          )}

          <Button
            type="submit"
            mx="auto"
            mt="xl"
            style={{ display: 'block' }}
            loading={isUpdating}
          >
            {t('account.update')}
          </Button>
        </form>
      ) : (
        <Text fs="italic" c="dimmed">
          {t('account.loadError')}
        </Text>
      )}
    </Container>
  );
}
